import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useProjectContext } from '../contexts/ProjectContext';
import { useChatId } from '../hooks/useChatId';
import { useChatHistory } from '../hooks/useChatHistory';
import { useChatApi } from '../hooks/useChatApi';
import { useLoadingSteps } from '../hooks/useLoadingSteps';
import { useChatList } from '../hooks/useChatList';
import { useRequireOnboarding } from '../hooks/useRequireOnboarding';

// Layout & Components
import MainLayout from '../components/Layout/MainLayout';
import AiDiagnosisPanel from '../components/Board/AiDiagnosisPanel';
import ChatPanel from '../components/Board/ChatPanel';
import AiSuggestionPanel from '../components/Board/AiSuggestionPanel';

// Types
import type { Message } from '../types/chat';
import type { DiagnosisId } from '../types/llm-diagnosis';

import styles from '../assets/styles/Board.module.scss';

const Board: React.FC = () => {
  // --- 1. Global Context & Auth ---
  const { selectedProjectId, isProjectReady } = useProjectContext();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isNewChat = pathname === '/board' || pathname === '/board/';
  const { showExternalLinkWarning } = useRequireOnboarding({
    projectId: selectedProjectId,
    projectReady: isProjectReady,
    requireAdSettings: true,
    redirectTo: '/onboarding',
    warnIfExternalLinkMissing: true,
  });

  const [activeDiagnosisId, setActiveDiagnosisId] = useState<DiagnosisId | null>(null);

  // --- 2. Date Management (For compatibility with existing hooks) ---
  const today = useMemo(() => new Date(), []);
  const firstDay = useMemo(() => new Date(today.getFullYear(), today.getMonth(), 1), [today]);
  const yesterday = useMemo(() => {
    const d = new Date(today);
    d.setDate(today.getDate() - 1);
    return d;
  }, [today]);

  const [startD, setStartD] = useState<Date>(firstDay);
  const [endD, setEndD] = useState<Date>(yesterday);

  // --- 2.5. Period Selector States ---
  const [selectedPeriod, setSelectedPeriod] = useState<string>('last_30_days');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  // --- 3. Chat State (Messages, ID, Input) ---
  const { chatId, setChatId, commitChatIdToUrl } = useChatId();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isSuggestionMode, setIsSuggestionMode] = useState(false);

  // --- 4. Suggestion & Follow-up State ---
  const [generateSuggestionId, setGenerateSuggestionId] = useState<number | null>(null);

  const [followups, setFollowups] = useState<{
    q1: string | null;
    q2: string | null;
    q3: string | null;
  }>({
    q1: null,
    q2: null,
    q3: null,
  });
  const [followupsVisible, setFollowupsVisible] = useState(false);
  const [followupsLeaving, setFollowupsLeaving] = useState(false);

  // --- 4.5. Reference Data Modal State ---
  const [isRefModalOpen, setIsRefModalOpen] = useState(false);
  const [selectedRefMsg, setSelectedRefMsg] = useState<Message | null>(null);

  // 日付計算 useEffect
  useEffect(() => {
    const today = new Date();
    let s = new Date();
    let e = new Date();

    if (selectedPeriod === 'last_30_days') {
      s.setDate(today.getDate() - 30);
      e.setDate(today.getDate() - 1);
    } else if (selectedPeriod === 'this_month') {
      s = new Date(today.getFullYear(), today.getMonth(), 1);
      e = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      if (e > today) {
        e.setDate(today.getDate() - 1);
      }
    } else if (selectedPeriod === 'last_month') {
      s = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      e = new Date(today.getFullYear(), today.getMonth(), 0);
    } else if (selectedPeriod === 'this_week') {
      const day = today.getDay();
      const diff = today.getDate() - day + (day === 0 ? -6 : 1);
      s = new Date(today.getFullYear(), today.getMonth(), diff);
      e = new Date();
      e.setDate(e.getDate() - 1);
    } else if (selectedPeriod === 'custom') {
      if (customStart && customEnd) {
        s = new Date(customStart);
        e = new Date(customEnd);
      } else {
        s.setDate(today.getDate() - 30);
        e.setDate(today.getDate() - 1);
      }
    }

    setStartD(s);
    setEndD(e);
  }, [selectedPeriod, customStart, customEnd]);

  const prevProjectIdRef = useRef<number | null | undefined>(undefined);

  // --- 5. Custom Hooks (Business Logic) ---
  // チャット履歴一覧（ドロップダウン用）のフック接続
  const { items: chatList, refetch: refetchChatList } = useChatList(selectedProjectId);

  const dummySetRange = useCallback(
    (r: any[]) => {
      if (r && r[0]) {
        const s = r[0].startDate ? new Date(r[0].startDate) : null;
        const e = r[0].endDate ? new Date(r[0].endDate) : null;
        if (s && e && !isNaN(s.getTime()) && !isNaN(e.getTime())) {
          setSelectedPeriod('custom');
          const formatStr = (d: Date) => {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${day}`;
          };
          setCustomStart(formatStr(s));
          setCustomEnd(formatStr(e));
        }
      }
    },
    [setSelectedPeriod, setCustomStart, setCustomEnd]
  );

  const { fetchChatHistory } = useChatHistory({
    selectedProjectId,
    setMessages,
    setStartD,
    setEndD,
    setRange: dummySetRange,
  });

  const formatToApiDate = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const { sendMsgRequest } = useChatApi({
    selectedProjectId,
    startD,
    endD,
    chatId,
    formatToApiDate,
  });

  const isGenerating = messages.some((m) => m.sender === 'bot' && m.isLoading);

  // Loading Animation Hook
  useLoadingSteps({
    isLoading: isGenerating,
    onTick: (nextText) => {
      setMessages((prev) => {
        const updated = [...prev];
        const idx = updated.findIndex((m) => m.sender === 'bot' && m.isLoading);
        if (idx === -1) return prev;
        updated[idx] = { ...updated[idx], text: nextText };
        return updated;
      });
    },
  });

  // Handle Project Change & Fetch Chat History
  useEffect(() => {
    const hasProjectChanged = selectedProjectId !== prevProjectIdRef.current;
    const isInitialLoad =
      (prevProjectIdRef.current === undefined || prevProjectIdRef.current === null) &&
      selectedProjectId !== null;
    if (hasProjectChanged) {
      prevProjectIdRef.current = selectedProjectId;
      if (!isInitialLoad && selectedProjectId) {
        setChatId(null);
        navigate('/board', { replace: true });
        setMessages([]);
        setFollowups({ q1: null, q2: null, q3: null });
        setInput('');
        setIsSuggestionMode(false);
        return;
      }
    }
    if (isNewChat) {
      setMessages([]);
      setFollowups({ q1: null, q2: null, q3: null });
      setIsSuggestionMode(false);
      return;
    }
    if (chatId) {
      fetchChatHistory(chatId);
    } else {
      setMessages([]);
      setFollowups({ q1: null, q2: null, q3: null });
      setIsSuggestionMode(false);
    }
  }, [selectedProjectId, chatId, isNewChat, fetchChatHistory, navigate, setChatId]);

  // Follow-up animation control
  useEffect(() => {
    if (followups.q1 || followups.q2 || followups.q3) {
      setFollowupsLeaving(false);
      requestAnimationFrame(() => setFollowupsVisible(true));
    } else {
      setFollowupsVisible(false);
    }
  }, [followups]);

  const calcRangeFromPeriod = (periodValue: number) => {
    const pv = Math.max(1, Math.floor(periodValue));
    const end = new Date(yesterday);
    const start = new Date(yesterday);
    start.setDate(start.getDate() - (pv - 1));
    return { start, end };
  };

  const applyCalendarRange = (start: Date, end: Date) => {
    setStartD(start);
    setEndD(end);
    setSelectedPeriod('custom');
    const formatStr = (d: Date) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    setCustomStart(formatStr(start));
    setCustomEnd(formatStr(end));
  };

  // Actions
  const handleSendMessage = async (
    text: string,
    opts?: {
      periodDays?: number | null;
      diagnosisId?: DiagnosisId | null;
      aiSuggestionMode?: boolean;
    }
  ) => {
    if (!text.trim() || isGenerating) return;

    // 1. Check Date override
    let overrideDates: { startD?: Date; endD?: Date } | undefined;
    if (opts?.periodDays != null) {
      const { start, end } = calcRangeFromPeriod(opts.periodDays);
      applyCalendarRange(start, end);
      overrideDates = { startD: start, endD: end };
    }
    // User message
    const userMsg: Message = {
      sender: 'user',
      text,
      aiSuggestionMode: opts?.aiSuggestionMode ?? false,
    };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');

    // Bot placeholder
    const botPlaceholder: Message = { sender: 'bot', text: '', isLoading: true };
    setMessages((prev) => [...prev, botPlaceholder]);

    try {
      const response = await sendMsgRequest(
        text,
        overrideDates,
        opts?.diagnosisId,
        opts?.aiSuggestionMode ?? false,
      );
      if (opts?.aiSuggestionMode) {
        setIsSuggestionMode(false);
      }
      // Update bot message
      setMessages((prev) => {
        const updated = [...prev];
        const idx = updated.findIndex((m) => m.sender === 'bot' && m.isLoading);
        if (idx !== -1) {
          updated[idx] = {
            sender: 'bot',
            text: response.answer,
            animate: true,
          };
        }
        return updated;
      });

      // Update follow-up questions
      setFollowups({
        q1: response.followup_questions_1 ?? null,
        q2: response.followup_questions_2 ?? null,
        q3: response.followup_questions_3 ?? null,
      });

      if (!chatId && response.chat_id) {
        setChatId(response.chat_id);
        navigate(`/board/${response.chat_id}`, { replace: true });
        refetchChatList(); // 新規チャット開始時に履歴を再読込
      }

      if (response.suggestion_id) {
        setGenerateSuggestionId(response.suggestion_id);
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      setMessages((prev) => {
        const updated = [...prev];
        const idx = updated.findIndex((m) => m.sender === 'bot' && m.isLoading);
        if (idx !== -1) {
          updated[idx] = {
            sender: 'bot',
            text: '通信エラーが発生しました。もう一度お試しください。',
          };
        }
        return updated;
      });
      setFollowups({ q1: null, q2: null, q3: null });
    }
  };

  const handleFollowupClick = async (text: string) => {
    setFollowupsLeaving(true);
    setFollowupsVisible(false);
    setTimeout(() => {
      setFollowups({ q1: null, q2: null, q3: null });
      setFollowupsLeaving(false);
    }, 1000);
    await handleSendMessage(text);
  };

  // チャット履歴削除処理
  const handleDeleteChat = async (targetChatId: string) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/delete-chat-history?project_id=${selectedProjectId}&chat_id=${targetChatId}`,
        { method: 'DELETE', credentials: 'include' }
      );
      if (!res.ok) throw new Error('削除に失敗しました。');
      await refetchChatList();
      if (chatId === targetChatId) {
        setChatId(null);
        navigate('/board', { replace: true });
      }
    } catch (err) {
      console.error('Failed to delete chat:', err);
      alert('チャットの削除に失敗しました。');
    }
  };

  // チャットタイトル変更処理
  const handleRenameChat = async (targetChatId: string, newTitle: string) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/update-chat-title?project_id=${selectedProjectId}&chat_id=${encodeURIComponent(targetChatId)}&title=${encodeURIComponent(newTitle)}`,
        { method: 'POST', credentials: 'include' }
      );
      if (!res.ok) throw new Error('タイトルの変更に失敗しました。');
      await refetchChatList();
    } catch (err) {
      console.error('Failed to rename chat:', err);
      alert('タイトルの変更に失敗しました。');
    }
  };

  const hasSuggestionPanel = true;
  const contentAreaClassName = hasSuggestionPanel
    ? `${styles.contentArea} ${styles.contentAreaWithSuggestion}`
    : styles.contentArea;

  return (
    <>
      {showExternalLinkWarning && (
        <div
          style={{
            background: 'rgba(242, 6, 93, 0.9)',
            color: '#fff',
            padding: '15px 20px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 10000,
            position: 'fixed',
            bottom: 20,
            right: 20,
            fontSize: 13,
            display: 'flex',
            alignItems: 'center',
            gap: '15px',
          }}
        >
          <span>広告アカウント（Google/Meta等）との連携が未設定です。</span>
          <a
            href="/project-setting/external"
            style={{
              color: '#fff',
              textDecoration: 'underline',
              fontWeight: 'bold',
              whiteSpace: 'nowrap',
            }}
          >
            連携設定画面へ
          </a>
        </div>
      )}
      <MainLayout fullBleed>
        <div className={contentAreaClassName}>
          <div className={styles.diagnosisPanel}>
            <AiDiagnosisPanel
              onQuickSend={(payload) => {
                setIsSuggestionMode(true);
                void handleSendMessage(payload.text, {
                  periodDays: payload.periodDays,
                  diagnosisId: payload.diagnosisId,
                  aiSuggestionMode: true,
                });
              }}
              activeDiagnosisId={activeDiagnosisId}
              setActiveDiagnosisId={setActiveDiagnosisId}
            />
          </div>

          <div className={styles.chatPanel}>
            <ChatPanel
              messages={messages}
              input={input}
              setInput={setInput}
              onSend={handleSendMessage}
              isGenerating={isGenerating}
              isSuggestionMode={isSuggestionMode}
              onSuggestionModeChange={setIsSuggestionMode}
              chatId={chatId}
              chatList={chatList}
              onDeleteChat={handleDeleteChat}
              onRenameChat={handleRenameChat}
              followups={followups}
              followupsVisible={followupsVisible}
              followupsLeaving={followupsLeaving}
              onFollowupClick={handleFollowupClick}
              onShowRef={(m) => {
                setSelectedRefMsg(m);
                setIsRefModalOpen(true);
              }}
              selectedPeriod={selectedPeriod}
              setSelectedPeriod={setSelectedPeriod}
              customStart={customStart}
              setCustomStart={setCustomStart}
              customEnd={customEnd}
              setCustomEnd={setCustomEnd}
            />
          </div>

          {hasSuggestionPanel && (
            <div className={styles.suggestionPanel}>
              <AiSuggestionPanel suggestionId={generateSuggestionId} chatId={chatId} />
            </div>
          )}
        </div>
      </MainLayout>

      {/* Reference Data Modal */}
      {isRefModalOpen && (
        <div className={styles.boardChatDataModalOverlay} onClick={() => setIsRefModalOpen(false)}>
          <div className={styles.boardChatDataModalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.boardChatDataModalHeader}>
              <h3>参照データ</h3>
              <button type="button" onClick={() => setIsRefModalOpen(false)}>
                ×
              </button>
            </div>
            <div className={styles.boardChatDataModalBody}>
              <p>この回答に対する参照データはありません。</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Board;
